interface PaginationOptions {
    container: HTMLElement;
    maxVisibleElements?: number;
    elementsBetweenDots?: number;
    enhancedMode?: boolean;
    showInput?: boolean;
    pageClickUrl?: string | ((pageNumber: number) => string);
    pageClickCallback?: (pageNumber: number) => void;
    callPageClickCallbackOnInit?: boolean;
    goToButtonLabel?: string;
    inputTitle?: string;
}

class PaginationComponent {
    private options: PaginationOptions;
    private paginationContainer: HTMLElement;
    private paginationUl?: HTMLElement;
    private goToPageInput?: HTMLInputElement;
    private maxVisibleElements: number;
    private elementsBetweenDots: number;
    private pageCount: number = 0;
    private currentPage: number = 1;

    constructor(options: PaginationOptions) {
        this.options = options;
        this.paginationContainer = options.container;
        this.maxVisibleElements = 13;

        // Устанавливаем количество элементов между точками
        this.elementsBetweenDots = options.elementsBetweenDots || 3; // Значение по умолчанию

        if (options.maxVisibleElements) {
            this.maxVisibleElements = options.maxVisibleElements;
            if (this.maxVisibleElements % 2 === 0) {
                this.maxVisibleElements--;
            }
            const enhancementCorrection = this.options.enhancedMode ? 4 : 0;
            if (this.maxVisibleElements - enhancementCorrection < 7) {
                this.maxVisibleElements = 7 + enhancementCorrection;
            }
        }

        // Проверяем, что maxVisibleElements достаточно для элементов между точками
        const minRequired = 7 + (this.elementsBetweenDots - 3); // Базовая формула
        if (this.maxVisibleElements < minRequired) {
            this.maxVisibleElements = minRequired;
        }
    }

    public make(itemsCount: number, itemsOnPage: number, defaultPageNumber: number = 1): void {
        defaultPageNumber = Number(defaultPageNumber);
        if (!defaultPageNumber) {
            defaultPageNumber = 1;
        }

        this.pageCount = Math.ceil(itemsCount / itemsOnPage);

        while (this.paginationContainer.firstChild) {
            this.paginationContainer.removeChild(this.paginationContainer.firstChild);
        }

        const innerContainer = document.createElement("div");
        innerContainer.classList.add("pagination-container");
        innerContainer.append(this.createPageList());

        if (this.options.showInput) {
            innerContainer.append(this.createPageInput());
        }

        this.paginationContainer.append(innerContainer);
        this.updateCurrentPage(defaultPageNumber, this.options.callPageClickCallbackOnInit);
    }

    public goToPage(pageNumber: number): void {
        if (pageNumber < 1) {
            pageNumber = 1;
        } else if (pageNumber > this.pageCount) {
            pageNumber = this.pageCount;
        }

        this.updateCurrentPage(pageNumber, true);

        if (this.options.pageClickUrl) {
            window.location.href = this.createPageClickUrl(pageNumber);
        }
    }

    public getPageCount(): number {
        return this.pageCount;
    }

    public getCurrentPage(): number {
        return this.currentPage;
    }

    private updateCurrentPage(newPageNumber: number, callPageClickCallback?: boolean): void {
        this.currentPage = newPageNumber;
        this.updateVisiblePageElements();

        if (this.options.showInput && this.goToPageInput) {
            this.goToPageInput.value = newPageNumber.toString();
        }

        if (callPageClickCallback && this.options.pageClickCallback) {
            this.options.pageClickCallback(newPageNumber);
        }
    }

    private createPageList(): HTMLElement {
        const paginationUl = document.createElement("ul");
        paginationUl.classList.add("pagination");
        paginationUl.classList.add("pagination-sm");
        this.paginationUl = paginationUl;
        return paginationUl;
    }

    private createPageElement(label: string, pageNumber: number): HTMLLIElement {
        const pageLi = document.createElement("li");
        pageLi.classList.add("page-item");

        const pageLink = document.createElement("a");
        pageLink.classList.add("page-link");
        pageLink.innerHTML = label;
        pageLink.setAttribute("data-page-number", pageNumber.toString());
        pageLink.addEventListener("click", this.onPageClick.bind(this));

        const pageClickUrl = this.options.pageClickUrl;
        const hrefUrl = pageClickUrl ? this.createPageClickUrl(pageNumber) : "#";
        pageLink.setAttribute("href", hrefUrl);

        pageLi.appendChild(pageLink);
        return pageLi;
    }

    private createDotsPageElement(): HTMLLIElement {
        const element = document.createElement("li");
        element.classList.add("disabled");
        element.classList.add("three-dots");

        const contentElement = document.createElement("span");
        contentElement.innerHTML = "&hellip;";
        element.appendChild(contentElement);

        return element;
    }

    private recreatePageElements(pageNumber: number): void {
        if (!this.paginationUl) return;

        const pageCount = this.pageCount;
        const isEnhanced = this.options.enhancedMode ?? false;
        const previousPage = pageNumber > 2 ? pageNumber - 1 : 1;
        const nextPage = pageNumber < pageCount ? pageNumber + 1 : pageCount;

        const previousPageLi = this.createPageElement(
            `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
        <path d="M8.5 1.25L1.5 7.75L8.5 14.25" stroke="#1D1C1C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
            previousPage
        );

        const nextPageLi = this.createPageElement(
            `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="16" viewBox="0 0 10 16" fill="none">
        <path d="M1.5 1.25L8.5 7.75L1.5 14.25" stroke="#1D1C1C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`,
            nextPage
        );

        const createAndAppendPageElement = (createPageNumber: number): void => {
            const pageLi = this.createPageElement(createPageNumber.toString(), createPageNumber);
            if (createPageNumber === pageNumber) {
                pageLi.classList.add("active");
            }
            this.paginationUl!.append(pageLi);
        };

        while (this.paginationUl.firstChild) {
            this.paginationUl.removeChild(this.paginationUl.firstChild);
        }

        if (pageCount <= this.maxVisibleElements - 2) {
            this.paginationUl.append(previousPageLi);
            for (let i = 1; i <= pageCount; i++) {
                createAndAppendPageElement(i);
            }
            this.paginationUl.append(nextPageLi);
            return;
        }

        // Используем настройку elementsBetweenDots
        const centerCount = this.elementsBetweenDots;
        const sideCount = Math.floor(centerCount / 2);
        let centerLeftPage = pageNumber - sideCount;
        let centerRightPage = pageNumber + sideCount;

        // Корректируем границы
        if (centerLeftPage < 2) {
            centerLeftPage = 2;
            centerRightPage = centerLeftPage + centerCount - 1;
        }

        if (centerRightPage > pageCount - 1) {
            centerRightPage = pageCount - 1;
            centerLeftPage = centerRightPage - centerCount + 1;
        }

        const showDotsLeft = centerLeftPage > 2;
        const showDotsRight = centerRightPage < pageCount - 1;

        this.paginationUl.append(previousPageLi);
        createAndAppendPageElement(1);

        if (showDotsLeft) {
            this.paginationUl.append(this.createDotsPageElement());
        }

        // Отображаем центральный блок с заданным количеством элементов
        for (let i = centerLeftPage; i <= centerRightPage; i++) {
            createAndAppendPageElement(i);
        }

        if (showDotsRight) {
            this.paginationUl.append(this.createDotsPageElement());
        }

        createAndAppendPageElement(pageCount);
        this.paginationUl.append(nextPageLi);
    }

    private updateVisiblePageElements(): void {
        this.recreatePageElements(this.currentPage);
    }

    private createPageInput(): HTMLElement {
        const inputGroupDiv = document.createElement("div");
        const goToPageInput = document.createElement("input");
        const goToPageButton = document.createElement("button");

        inputGroupDiv.classList.add("input-group");
        inputGroupDiv.classList.add("input-group-sm");
        inputGroupDiv.classList.add("pagination-input");
        inputGroupDiv.append(goToPageInput);
        inputGroupDiv.append(goToPageButton);

        goToPageInput.setAttribute("type", "text");
        goToPageInput.classList.add("form-control");
        goToPageInput.addEventListener("keydown", this.onGoToInputKeyPress.bind(this));

        goToPageButton.setAttribute("type", "button");
        goToPageButton.classList.add("btn");
        goToPageButton.classList.add("btn-outline-secondary");
        goToPageButton.innerHTML = this.options.goToButtonLabel === undefined ? "&#10140;" : this.options.goToButtonLabel;
        goToPageButton.addEventListener("click", this.onGoToPageButtonClick.bind(this));

        if (this.options.inputTitle) {
            goToPageInput.setAttribute("title", this.options.inputTitle);
            goToPageButton.setAttribute("title", this.options.inputTitle);
        }

        this.goToPageInput = goToPageInput;
        return inputGroupDiv;
    }

    private onPageClick(event: Event): void {
        const target = event.target as HTMLElement;
        const navigateElement = target.closest('.page-item')?.querySelector('[data-page-number]') as HTMLElement;

        if (!navigateElement) return;

        const pageValue = navigateElement.dataset.pageNumber;
        const pageNumber = Number(pageValue);

        if (this.options.pageClickUrl) {
            if (this.options.pageClickCallback) {
                this.options.pageClickCallback(pageNumber);
            }
            return;
        }

        event.preventDefault();
        this.updateCurrentPage(pageNumber, true);
    }

    private onGoToPageButtonClick(): void {
        if (!this.goToPageInput) return;

        const pageNumberData = this.goToPageInput.value;
        const pageNumber = Number(pageNumberData);
        this.goToPage(pageNumber);
    }

    private onGoToInputKeyPress(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            this.onGoToPageButtonClick();
        }
    }

    private createPageClickUrl(pageNumber: number): string {
        const pageClickUrl = this.options.pageClickUrl;

        switch (typeof pageClickUrl) {
            case "function":
                return pageClickUrl(pageNumber);
            case "string":
                return pageClickUrl.replace("{{page}}", pageNumber.toString());
            default:
                return "#";
        }
    }
}

// Экспорт для использования в других модулях
export default PaginationComponent;
